import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDriveById } from '../../api/drivesApi';
import { getAssessments, updateAssessment, regenerateToken } from '../../api/assessmentsApi';
import { FaCopy, FaSyncAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const BASE_FRONTEND_URL = window.location.origin || process.env.REACT_APP_FRONTEND_URL;

const formatCreatedDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
};

const ShareLinkPage = () => {
  const { driveId } = useParams();
  const [drive, setDrive] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [driveRes, assessRes] = await Promise.all([
        getDriveById(driveId),
        getAssessments()
      ]);
      setDrive(driveRes.data);
setAssessments(assessRes.data.filter(a => a.driveId === driveId));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [driveId]);

  const handleCopy = (token) => {
    const link = `${BASE_FRONTEND_URL}/assess/${token}`;
    navigator.clipboard.writeText(link);
    showCustomAlert('Link copied to clipboard!');
  };

  const handleRegenerate = async (assessmentId) => {
    if (window.confirm('Regenerating the token will invalidate the old link. Continue?')) {
      try {
        await regenerateToken(assessmentId);
        fetchData();
      } catch (err) {
        showCustomAlert('Failed to regenerate token');
      }
    }
  };

  const handleToggle = async (assessment, field) => {
    try {
      await updateAssessment(assessment.id, {
        [field]: !assessment[field]
      });
      fetchData();
    } catch (err) {
      showCustomAlert('Failed to update assessment');
    }
  };

  if (loading) return <div className="p-5 text-center"><div className="spinner-border"></div></div>;
  if (!drive) return <div className="p-5 text-center">Drive not found.</div>;

  return (
    <div className="container-fluid p-0">
      <h3 className="fw-bold mb-4">Share Links - {drive.name}</h3>

      {assessments.length === 0 ? (
        <div className="alert alert-warning">No assessments found for this drive. Please create an assessment first.</div>
      ) : (
        <div className="row g-4">
          {assessments.map(assessment => (
            <div key={assessment.id} className="col-md-6">
              <div className="card border-0 shadow-sm rounded-4 h-100">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <h5 className="fw-bold text-primary-custom mb-0">{assessment.name}</h5>
                    {formatCreatedDate(assessment.createdAt) && (
                      <span className="text-muted small text-nowrap" title="Created date">
                        Created: {formatCreatedDate(assessment.createdAt)}
                      </span>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <label className="text-muted small fw-bold">Registration Link</label>
                    <div className="input-group mt-1 mb-2">
                      <input 
                        type="text" 
                        className="form-control bg-light" 
                        value={assessment.token ? `${BASE_FRONTEND_URL}/register/${assessment.token}` : 'Token not generated'} 
                        readOnly 
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        onClick={() => {
                          navigator.clipboard.writeText(`${BASE_FRONTEND_URL}/register/${assessment.token}`);
                          showCustomAlert('Registration Link copied!');
                        }}
                        disabled={!assessment.token}
                        title="Copy Registration Link"
                      >
                        <FaCopy />
                      </button>
                    </div>
                    <label className="text-muted small fw-bold mt-2">Assessment Link</label>
                    <div className="input-group mt-1">
                      <input 
                        type="text" 
                        className="form-control bg-light" 
                        value={assessment.token ? `${BASE_FRONTEND_URL}/assess/${assessment.token}` : 'Token not generated'} 
                        readOnly 
                      />
                      <button 
                        className="btn btn-outline-secondary" 
                        onClick={() => {
                          navigator.clipboard.writeText(`${BASE_FRONTEND_URL}/assess/${assessment.token}`);
                          showCustomAlert('Assessment Link copied!');
                        }}
                        disabled={!assessment.token}
                        title="Copy Assessment Link"
                      >
                        <FaCopy />
                      </button>
                      <button 
                        className="btn btn-outline-danger" 
                        onClick={() => handleRegenerate(assessment.id)}
                        title="Regenerate Token"
                      >
                        <FaSyncAlt />
                      </button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="d-flex flex-column">
                      <span className="fw-semibold">Registration Status</span>
                      {assessment.registrationOpen ? (
                        <span className="text-success small d-flex align-items-center gap-1"><FaCheckCircle /> Open</span>
                      ) : (
                        <span className="text-danger small d-flex align-items-center gap-1"><FaTimesCircle /> Closed</span>
                      )}
                    </div>
                    <button 
                      className={`btn btn-sm ${assessment.registrationOpen ? 'btn-danger' : 'btn-success'}`}
                      onClick={() => handleToggle(assessment, 'registrationOpen')}
                    >
                      {assessment.registrationOpen ? 'Disable Link' : 'Enable Link'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default ShareLinkPage;
